import React, { useEffect, useState } from 'react'
import { Button, Card, CardBody, CardHeader, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap'
import McDataServices from '../../services/McDataServices'
import { useTranslation } from 'react-i18next'

const PackageDetailsModal = ({ isOpen, toggle, packageId }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [pkg, setPkg] = useState(null)

  useEffect(() => {
    if (isOpen && packageId) {
      fetchPackage()
    }
  }, [isOpen, packageId])

  const fetchPackage = async () => {
    setLoading(true)
    try {
      const data = await McDataServices.getPackageById(packageId)
      setPkg(data)
    } catch (error) {
      console.error('Failed to fetch package', error)
    } finally {
      setLoading(false)
    }
  }

  const renderRule = (rule) => {
    const value = rule.parameters?.count ?? rule.parameters?.allowedHsCodes ?? rule.parameters?.allowedSearchCountries ?? '-'
    return (
      <Col xl="4" lg="6" md="12" key={rule.type} className="mb-2">
        <div className="d-flex flex-column">
          <span className="modern-label d-block text-truncate" title={t(`payment.rules.${rule.type}`, rule.type)}>
            {t(`payment.rules.${rule.type}`, rule.type)}
          </span>
          <div className="modern-value-box">
            {value}
          </div>
        </div>
      </Col>
    )
  }

  const isMailingRule = (type) => {
    return type.startsWith('B2B_') ||
      ['AI_REL_CHECK', 'AI_EMAIL_GEN', 'CONTACT_FIRM_SEARCH', 'CONTACT_EMAIL_SEARCH', 'FOLLOW_UP'].includes(type)
  }

  const formatDuration = () => {
    if (!pkg) return '-'
    const parts = []
    if (pkg.durationYear) parts.push(`${pkg.durationYear} ${t('admin.duration.year', 'Yıl')}`)
    if (pkg.durationMonth) parts.push(`${pkg.durationMonth} ${t('admin.duration.month', 'Ay')}`)
    if (pkg.durationDay) parts.push(`${pkg.durationDay} ${t('admin.duration.day', 'Gün')}`)
    return parts.join(' ') || '-'
  }

  const generalRules = pkg?.rules?.filter(r => !isMailingRule(r.type)) || []
  const mailingRules = pkg?.rules?.filter(r => isMailingRule(r.type)) || []

  return (
    <>
      <style>
        {`
          .super-wide-modal .modal-content {
            background-color: #f3f4f6 !important;
            border: none !important;
            border-radius: 8px !important;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
          }
          .super-wide-modal {
            max-width: 95vw !important;
            width: 95vw !important;
          }
          .modern-card {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
          .modern-card-header {
            background-color: transparent !important;
            border-bottom: none !important;
            padding: 0.5rem 0.25rem 1rem 0.25rem !important;
          }
          .modern-card-body {
             padding: 0.25rem !important;
          }
          .modern-label {
            font-size: 0.7rem !important;
            font-weight: 600 !important;
            color: #6b7280 !important;
            margin-bottom: 0.4rem !important;
          }
          .modern-value-box {
            background-color: #ffffff !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 4px !important;
            padding: 0.375rem 0.75rem !important;
            font-size: 0.8125rem !important;
            color: #374151 !important;
            font-weight: 600 !important;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
            min-height: 34px !important;
            display: flex;
            align-items: center;
          }
          .modal-header-modern {
             background-color: #e5e7eb !important;
             border-bottom: none !important;
             padding: 0.75rem 1.25rem !important;
             border-radius: 8px 8px 0 0 !important;
          }
          .modal-footer-modern {
             background-color: #e5e7eb !important;
             border-top: none !important;
             padding: 0.75rem 1.25rem !important;
             border-radius: 0 0 8px 8px !important;
          }
          .btn-modern-secondary {
             background-color: #4b5563 !important;
             border-color: #4b5563 !important;
             color: #ffffff !important;
             border-radius: 4px !important;
             font-weight: 500 !important;
             padding: 0.375rem 1.5rem !important;
          }
          .section-title {
             background: #e5e7eb;
             padding: 0.35rem 0.85rem;
             border-radius: 6px;
             margin-bottom: 0.5rem;
             display: inline-block;
          }
        `}
      </style>
      <Modal isOpen={isOpen} toggle={toggle} className="modal-dialog-centered super-wide-modal" size="xl">
        <ModalHeader toggle={toggle} className="modal-header-modern">
          <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem', color: '#374151' }}>{t('admin.modal.viewPackage', 'Paket Detayları')}</h6>
        </ModalHeader>
        <ModalBody className="p-4">
          {loading
            ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner color="primary" size="sm"/>
              </div>
              )
            : pkg
              ? (
                <>
                  <Card className="mb-4 modern-card">
                    <CardBody className="modern-card-body position-relative">
                      <div style={{ position: 'absolute', left: '-0.5rem', top: 0, bottom: 0, width: '4px', backgroundColor: '#3b82f6', borderRadius: '2px' }}></div>
                      <div className="d-flex align-items-center flex-wrap" style={{ gap: '2rem' }}>
                        <div>
                          <span className="modern-label d-block">{t('admin.table.packageName', 'Paket Adı')}</span>
                          <div className="modern-value-box text-primary">{pkg.name}</div>
                        </div>
                        <div>
                          <span className="modern-label d-block">{t('admin.table.price', 'Fiyat')}</span>
                          <div className="modern-value-box">{pkg.price} {pkg.currency}</div>
                        </div>
                        <div>
                          <span className="modern-label d-block">{t('admin.table.duration', 'Süre')}</span>
                          <div className="modern-value-box">{formatDuration()}</div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Row className="gx-4">
                    <Col md="6">
                      <Card className="mb-0 modern-card h-100">
                        <CardHeader className="modern-card-header">
                          <div className="section-title">
                            <h6 className="mb-0 fw-bold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#4b5563' }}>
                              {t('admin.modal.generalSubscription', 'Genel Abonelik')}
                            </h6>
                          </div>
                        </CardHeader>
                        <CardBody className="modern-card-body">
                          <Row className="gx-3 gy-3">
                            {generalRules.length > 0
                              ? generalRules.map(renderRule)
                              : <Col><p className="text-muted small mb-0">{t('admin.modal.noRules', 'Kural tanımlanmamış.')}</p></Col>
                            }
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md="6">
                      <Card className="mb-0 modern-card h-100">
                        <CardHeader className="modern-card-header">
                          <div className="section-title">
                            <h6 className="mb-0 fw-bold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#4b5563' }}>
                              {t('admin.modal.mailingSubscription', 'Mailing Aboneliği')}
                            </h6>
                          </div>
                        </CardHeader>
                        <CardBody className="modern-card-body">
                          <Row className="gx-3 gy-3">
                            {mailingRules.length > 0
                              ? mailingRules.map(renderRule)
                              : <Col><p className="text-muted small mb-0">{t('admin.modal.noMailingRules', 'Mailing kuralı tanımlanmamış.')}</p></Col>
                            }
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </>
                )
              : null}
        </ModalBody>
        <ModalFooter className="modal-footer-modern">
          <Button className="btn-modern-secondary" onClick={toggle}>
            {t('admin.modal.close', 'Kapat')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default PackageDetailsModal
